// =============================================================================
// PersistentFlowManager: Manages flows with persistence capabilities
// Automatically handles flow restoration on page refresh
// =============================================================================

import { _eventBus } from "../events/EventBus";
import { WorkFlowOrchestrator } from "./WorkFlowOrchestrator";

export class PersistentFlowManager {
    private orchestrator: WorkFlowOrchestrator<any>;

    constructor(orchestrator: WorkFlowOrchestrator<any>) {
        this.orchestrator = orchestrator;
    }

    // Execute flow with persistence - simplified logic
    async executeWithPersistence(flowName: string): Promise<string> {
        try {
            // Try to resume existing flow first
            if (this.shouldRestoreFlow(flowName)) {
                const runnerId = await this.orchestrator.resumeFlow(flowName);
                if (runnerId) {
                    return runnerId;
                }
            }

            // Clear any old state and start fresh
            this.orchestrator.clearPersistedFlow(flowName);
            return this.orchestrator.execute(flowName, true);

        } catch (error) {
            console.error(`[PersistentFlowManager] Error executing ${flowName}:`, error);
            // Clear corrupted state and start fresh
            this.orchestrator.clearPersistedFlow(flowName);
            return this.orchestrator.execute(flowName, true);
        }
    }

    // Cancel a persistent flow and clear its state
    cancelPersistedFlow(flowName: string): void {
        this.orchestrator.clearPersistedFlow(flowName);
    }

    // Simple check if flow should be restored
    private shouldRestoreFlow(flowName: string): boolean {
        return this.orchestrator.hasPersistedFlow(flowName);
    }
    // Clean up old persisted flows
    cleanup(): void {
        this.orchestrator.cleanupPersistedFlows();
    }
}

// Global instance for cancel booking flow management
let persistentFlowManager: PersistentFlowManager | null = null;

export function getPersistentFlowManager(orchestrator: WorkFlowOrchestrator<any>): PersistentFlowManager {
    if (!persistentFlowManager) {
        persistentFlowManager = new PersistentFlowManager(orchestrator);
    }
    return persistentFlowManager;
}